/** @format */
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { PlanValidationResult, PlanValidationTs } from 'common-typescript';
import {
    DegreeProgrammeAttainmentWorkflow,
    Module,
    ModuleType,
    OtmId,
    PlanValidationState,
    UniversitySettings,
    Workflow,
} from 'common-typescript/types';
import { combineLatest, map, Observable, take } from 'rxjs';
import { AppErrorHandler } from '../../error-handler';
import { STUDENT_WORKFLOW_TYPE } from '../../model';
import { UniversityService, WorkflowEntityService } from '../../service';

interface TemplateData {
    universitySettings: UniversitySettings;
    newestWorkflow: Workflow;
}

@Component({
    selector: 'sis-graduation-workflow-notification',
    standalone: false,
    templateUrl: './graduation-workflow-notification.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GraduationWorkflowNotificationComponent implements OnInit {
    @Input() module: Module;
    @Input({ required: true }) planValidationResult: PlanValidationResult;

    data$: Observable<TemplateData>;

    constructor(
        private readonly universityService: UniversityService,
        private readonly appErrorHandler: AppErrorHandler,
        private readonly workflowService: WorkflowEntityService,
        private readonly stateService: StateService,
    ) {}

    ngOnInit(): void {
        const universitySettings$ = this.createUniversitySettingsObservable();
        const newestWorkflow$ = this.createStudentWorkflowObservable();
        this.data$ = this.createDataObservable(universitySettings$, newestWorkflow$);
    }

    canGraduateFromSelectedModule(data: TemplateData): boolean {
        return this.isGraduationAllowed(data.universitySettings) && this.isModuleValidForGraduate(data.newestWorkflow);
    }

    graduationRequestedForSelectedModule(data: TemplateData): boolean {
        return (
            this.module?.type === ModuleType.DEGREE_PROGRAMME &&
            this.isApplicationRequested(data.newestWorkflow) &&
            !this.planValidationResult?.validatablePlan.isModuleAttained(this.module?.id)
        );
    }

    openGraduationDialog(): void {
        this.stateService.go('student.logged-in.graduation-wizard', {
            degreeProgrammeId: this.module.id,
            planId: this.planValidationResult?.validatablePlan?.plan?.id,
        });
    }

    navigateToWorkflow(workflowId: OtmId): void {
        this.stateService.go('student.logged-in.profile.applications.degree-programme-attainment-workflow', { applicationId: workflowId });
    }

    isApplicationApproved(workflow: Workflow): boolean {
        return workflow?.state === 'ACCEPTED';
    }

    private createDataObservable(
        universitySettings$: Observable<UniversitySettings>,
        newestWorkflow$: Observable<Workflow>,
    ): Observable<TemplateData> {
        return combineLatest([universitySettings$, newestWorkflow$]).pipe(
            map(([universitySettings, newestWorkflow]) => ({ universitySettings, newestWorkflow })),
        );
    }

    private createUniversitySettingsObservable(): Observable<UniversitySettings> {
        return this.universityService.getCurrentUniversitySettings().pipe(take(1), this.appErrorHandler.defaultErrorHandler());
    }

    private createStudentWorkflowObservable(): Observable<Workflow> {
        return this.workflowService.getWorkflowsByStudentId(this.planValidationResult?.validatablePlan?.plan?.userId).pipe(
            take(1),
            map(workflows => workflows.filter(workflow => workflow.type === STUDENT_WORKFLOW_TYPE.DEGREE_PROGRAMME_ATTAINMENT_WORKFLOW)),
            map((workflows: DegreeProgrammeAttainmentWorkflow[]) =>
                workflows.reduce<Workflow | undefined>((latest: Workflow, workflow: DegreeProgrammeAttainmentWorkflow) => {
                    if (workflow.moduleId !== this.module.id) {
                        return latest;
                    }
                    if (!latest) {
                        return workflow;
                    }
                    const latestTime = new Date(latest.metadata.lastModifiedOn).getTime();
                    const currentTime = new Date(workflow.metadata.lastModifiedOn).getTime();
                    return currentTime > latestTime ? workflow : latest;
                }, undefined),
            ),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    private isGraduationAllowed(universitySettings: UniversitySettings): boolean {
        return (
            this.isDegreeProgrammeAttainmentApplicationAllowed(universitySettings) &&
            this.planValidationResult?.validatablePlan.plan.primary
        );
    }

    // hasInvalidOrMissingSelections is intentionally not checked here: it isn't checked by any of the other
    // "can request graduation" implementations (old structure view, degree programme info modal), and for
    // professional further education (e.g. erikoislääkärikoulutus) it was incorrectly true even when the plan was
    // otherwise ready to graduate, hiding this notification while the equivalent buttons elsewhere showed correctly.
    private isModuleValidForGraduate(workflow: Workflow): boolean {
        return (
            this.module?.type === ModuleType.DEGREE_PROGRAMME &&
            this.planValidationResult?.validatablePlan?.studyRight?.state === 'ACTIVE' &&
            !this.isApplicationRequested(workflow) &&
            this.isModulePlanStateValidForGraduation() &&
            !this.planValidationResult.validatablePlan.isModuleAttained(this.module?.id)
        );
    }

    private isDegreeProgrammeAttainmentApplicationAllowed(universitySettings: UniversitySettings): boolean {
        return universitySettings?.frontendFeatureToggles.degreeProgrammeAttainmentApplicationEnabled ?? true;
    }

    private isApplicationRequested(workflow: Workflow): boolean {
        return workflow?.state === 'REQUESTED';
    }

    private isModulePlanStateValidForGraduation(): boolean {
        return [
            PlanValidationState.PARTS_ATTAINED,
            PlanValidationState.APPROVED_PARTS_ATTAINED,
            PlanValidationState.APPROVED_CONDITIONALLY_PARTS_ATTAINED,
        ].includes(
            PlanValidationTs.getPlanStateForModule(this.module, this.planValidationResult.validatablePlan, this.planValidationResult),
        );
    }
}
